import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { EstimateLineItems } from './EstimateLineItems';
import { useSendEstimate } from '@/hooks/useEstimates';
import type { Estimate } from '@/types/estimate';
import { Loader2, Send, Mail } from 'lucide-react';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface SendEstimateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  estimate: Estimate;
}

export function SendEstimateDialog({ 
  open, 
  onOpenChange, 
  estimate 
}: SendEstimateDialogProps) {
  const sendEstimate = useSendEstimate();
  const [message, setMessage] = useState('');

  const handleSend = () => {
    sendEstimate.mutate(
      { estimateId: estimate.id, message: message || undefined },
      {
        onSuccess: () => {
          onOpenChange(false);
          setMessage('');
        },
      }
    );
  };

  const { formatShortDate } = useCompanyLocale();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send Estimate {estimate.estimate_number}
          </DialogTitle>
          <DialogDescription>
            Review and send this estimate to {estimate.customer_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Recipient Info */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">To:</span>
              <span className="text-sm font-medium">{estimate.sent_to_email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-muted-foreground">Customer:</span>
              <span className="text-sm font-medium">{estimate.customer_name}</span>
            </div>
            {estimate.valid_until && (
              <div className="flex justify-between">
                <span className="text-sm text-muted-foreground">Valid Until:</span>
                <span className="text-sm font-medium">
                  {formatShortDate(estimate.valid_until)}
                </span>
              </div>
            )}
          </div>

          {/* Line Items Preview */}
          <div>
            <Label className="text-sm font-medium">Estimate Summary</Label>
            <div className="mt-2">
              <EstimateLineItems
                items={estimate.line_items}
                onChange={() => {}}
                readOnly
              />
            </div>
          </div>

          {/* Custom Message */}
          <div>
            <Label htmlFor="message">Custom Message (Optional)</Label>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a personal message to include in the email..."
              rows={3}
              className="mt-2"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            disabled={sendEstimate.isPending}
          >
            {sendEstimate.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Send Estimate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
